import React, { useState, useEffect } from 'react';
import { FileText, Check, X, Loader2, Package, ChevronLeft, Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { BookMockup, getBadge } from '../components/BookMockup';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import axios from 'axios';
import { createCustomBundleCheckout } from '../services/paymentService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_GUIDES = 5;
const BUNDLE_PRICE = 39;

const CATEGORY_FILTERS = [
  { key: 'all', label: 'All Categories' },
  { key: 'Credit Disputes & Credit Reporting', label: 'Credit Disputes' },
  { key: 'Business Credit & Business Setup', label: 'Business Credit' },
  { key: 'Court Motions & Legal Filings', label: 'Court Motions' },
  { key: 'Affidavits & Sworn Statements', label: 'Affidavits' },
  { key: 'Trust & Estate Planning', label: 'Trust & Estate' },
  { key: 'Legal Notices & Demand Letters', label: 'Legal Notices' },
  { key: 'Landlord-Tenant Disputes', label: 'Landlord-Tenant' },
];

const BundleBuilder = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [purchasing, setPurchasing] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const resp = await axios.get(`${API}/products`);
        const allGuides = (resp.data.products || []).filter(p => p.product_type === 'guide');
        setGuides(allGuides);
      } catch (err) {
        console.error('Error fetching guides:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);

  const toggleGuide = (guide) => {
    setSelected(prev => {
      const exists = prev.find(g => g.id === guide.id);
      if (exists) return prev.filter(g => g.id !== guide.id);
      if (prev.length >= MAX_GUIDES) return prev;
      return [...prev, guide];
    });
  };

  const removeGuide = (guideId) => {
    setSelected(prev => prev.filter(g => g.id !== guideId));
  };

  const handleCheckout = async () => {
    if (selected.length !== MAX_GUIDES) return;
    setPurchasing(true);
    try {
      const guideIds = selected.map(g => g.id);
      const session = await createCustomBundleCheckout(guideIds);
      if (session.url) window.location.href = session.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Unable to process checkout. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const filtered = guides.filter(g => {
    const matchesCategory = activeFilter === 'all' || g.category === activeFilter;
    const matchesSearch = !search || g.title.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const selectedIds = new Set(selected.map(g => g.id));
  const individualTotal = selected.reduce((sum, g) => sum + (g.price || 11.99), 0);
  const savings = individualTotal - BUNDLE_PRICE;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-indigo-700 via-blue-600 to-indigo-700 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4">
            <Package size={16} />
            <span className="text-sm font-semibold">Mix & Match Any 5 Guides</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3" data-testid="bundle-builder-title">
            Build Your Own Bundle
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-xl mx-auto">
            Pick any 5 guides from any category — pay only <span className="font-bold text-yellow-300">$39</span> instead of $60+
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Main Content — Guide Grid */}
          <div className="flex-1 min-w-0">
            {/* Search + Filters */}
            <div className="mb-6 space-y-4">
              <input
                type="text"
                placeholder="Search guides..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="bundle-builder-search"
              />
              <div className="flex flex-wrap gap-2">
                {CATEGORY_FILTERS.map(cat => (
                  <button
                    key={cat.key}
                    onClick={() => setActiveFilter(cat.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      activeFilter === cat.key
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`filter-${cat.key}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Results count */}
            <p className="text-sm text-gray-500 mb-4">{filtered.length} guides available</p>

            {loading && (
              <div className="flex justify-center py-20">
                <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
              </div>
            )}

            {/* Guide Grid */}
            {!loading && (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map(guide => {
                  const isSelected = selectedIds.has(guide.id);
                  const isFull = selected.length >= MAX_GUIDES && !isSelected;
                  return (
                    <button
                      key={guide.id}
                      onClick={() => !isFull && toggleGuide(guide)}
                      disabled={isFull}
                      className={`relative text-left rounded-xl border-2 overflow-hidden transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                          : isFull
                          ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md cursor-pointer'
                      }`}
                      data-testid={`guide-card-${guide.id}`}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 z-20 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center shadow-md">
                          <Check size={14} className="text-white" />
                        </div>
                      )}

                      <div className="relative h-32 overflow-hidden">
                        <BookMockup productId={guide.id} title={guide.title} size="card" badge={getBadge(guide)} />
                        <div className="absolute bottom-1 left-1 z-10">
                          <span className="text-[9px] bg-gray-800/70 text-white px-1.5 py-0.5 rounded">
                            {guide.category?.split('&')[0]?.trim()}
                          </span>
                        </div>
                      </div>

                      <div className="p-3">
                        <h3 className="text-xs font-bold text-gray-900 line-clamp-2 leading-tight mb-1">
                          {guide.title}
                        </h3>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">${guide.price}</span>
                          {isSelected ? (
                            <span className="text-[10px] font-bold text-blue-600">Selected</span>
                          ) : !isFull ? (
                            <span className="text-[10px] font-medium text-gray-400">Click to add</span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar — Selected Guides + Checkout */}
          <div className="lg:w-80 shrink-0">
            <div className="lg:sticky lg:top-20">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden" data-testid="bundle-builder-sidebar">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-bold text-lg">Your Bundle</h2>
                    <div className="bg-white/20 rounded-full px-2.5 py-0.5 text-sm font-bold">
                      {selected.length}/{MAX_GUIDES}
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div
                      className="bg-yellow-400 rounded-full h-2 transition-all duration-300"
                      style={{ width: `${(selected.length / MAX_GUIDES) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-100 mt-1.5">
                    {selected.length === 0 && 'Pick 5 guides from any category'}
                    {selected.length > 0 && selected.length < MAX_GUIDES && `${MAX_GUIDES - selected.length} more to go`}
                    {selected.length === MAX_GUIDES && 'Bundle complete! Ready to checkout'}
                  </p>
                </div>

                {/* Selected guides list */}
                <div className="p-4 space-y-2 min-h-[120px]">
                  {selected.length === 0 && (
                    <div className="text-center py-6">
                      <ShoppingBag className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">No guides selected yet</p>
                    </div>
                  )}
                  {selected.map((guide, idx) => (
                    <div key={guide.id} className="flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2 group" data-testid={`selected-guide-${guide.id}`}>
                      <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                        {idx + 1}
                      </div>
                      <p className="text-xs text-gray-800 font-medium line-clamp-1 flex-1 min-w-0">{guide.title}</p>
                      <button
                        onClick={() => removeGuide(guide.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors shrink-0"
                        data-testid={`remove-guide-${guide.id}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Pricing breakdown */}
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                  {selected.length > 0 && (
                    <div className="space-y-1 mb-3">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Individual total ({selected.length} guides)</span>
                        <span className="line-through">${individualTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold text-gray-900">
                        <span>Bundle price</span>
                        <span>${BUNDLE_PRICE}</span>
                      </div>
                      {savings > 0 && (
                        <div className="flex justify-between text-xs font-bold text-green-600">
                          <span>You save</span>
                          <span>${savings.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    className={`w-full py-5 text-base font-bold shadow-lg transition-all ${
                      selected.length === MAX_GUIDES
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={selected.length !== MAX_GUIDES || purchasing}
                    onClick={handleCheckout}
                    data-testid="bundle-builder-checkout-btn"
                  >
                    {purchasing ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                    ) : selected.length === MAX_GUIDES ? (
                      <>Checkout — ${BUNDLE_PRICE}</>
                    ) : (
                      <>Select {MAX_GUIDES - selected.length} more guide{MAX_GUIDES - selected.length !== 1 ? 's' : ''}</>
                    )}
                  </Button>

                  {selected.length > 0 && (
                    <button
                      onClick={() => setSelected([])}
                      className="w-full text-center text-xs text-gray-400 hover:text-red-500 mt-2 flex items-center justify-center gap-1 transition-colors"
                      data-testid="clear-all-btn"
                    >
                      <Trash2 size={11} /> Clear all
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile floating bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl p-3 z-40" data-testid="mobile-bundle-bar">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-bold text-gray-900">{selected.length}/{MAX_GUIDES} selected</div>
            {selected.length === MAX_GUIDES && (
              <div className="text-xs text-green-600 font-semibold">Save ${savings.toFixed(2)}</div>
            )}
          </div>
          <Button
            className={`px-6 py-3 font-bold ${
              selected.length === MAX_GUIDES
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-400'
            }`}
            disabled={selected.length !== MAX_GUIDES || purchasing}
            onClick={handleCheckout}
            data-testid="mobile-checkout-btn"
          >
            {purchasing ? 'Processing...' : selected.length === MAX_GUIDES ? `Checkout — $${BUNDLE_PRICE}` : `${MAX_GUIDES - selected.length} more`}
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BundleBuilder;
